import React, {useEffect, useState, useMemo, useRef} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState } from "../../app/RootReducer";
import { getProducts } from "./Slices/ProductSlice";
import { getProductsFilters } from "./Slices/ProductFilterSlice";
import { getTaxons } from "../Taxons/TaxonSlice";
import { GetCurrentLocale } from "../../components/Utils";
import PageTitle from "../../components/PageTitle";
import ProductsCatalogue from "./ProductsCatalogue";
import ProductsFilters from "./ProductsFilters";
import TaxonomiesProducts from "./TaxonomiesProducts";
import CategoriesSecurityEquipment from "./CategoriesSecurityEquipment";
import CategoriesIndividualEquipment from "./CategoriesIndividualEquipment";
import { SpinnerOnTop } from "../../components/SpinnerOnTop";
import {extractFiltersFromUrl} from "./Filters/UtilsFilters";
import {Filter} from "../Entity/Filter";
import SubTaxonomiesProducts from "./SubTaxonomiesProducts";

const ProductsListContainer = () => {
  const locale = GetCurrentLocale();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useNavigate();

  const products = useSelector((state: RootState) => state.productsRdc.products);
  const isRequestingGetProducts = useSelector((state: RootState) => state.productsRdc.isRequesting);
  const filters = useSelector((state: RootState) => state.filtersRdc.filters);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  const {
    filters: filtersSelected,
    view: defaultViewProduct,
    isEndOfStock,
    groupCode,
    groupValues
  } = useMemo(() => extractFiltersFromUrl(), [location.search]);

  const [newViewProduct, setNewViewProduct] = useState("thumb");

  useEffect(() => {
    setNewViewProduct(defaultViewProduct ?? "thumb");
  }, [defaultViewProduct]);

  useEffect(() => {
    const extracted = extractFiltersFromUrl();
    if (extracted.view && extracted.view !== newViewProduct) {
      setNewViewProduct(extracted.view);
    }
  }, [location.search]);

  const [txSelected, setTxSelected] = useState<string[]>(groupValues ?? []);
  const [txParent, setTxParent] = useState<string>(groupValues?.[0] ?? "");
  const [txGroupCode, setTxGroupCode] = useState<string>(groupCode ?? "");
  const effectiveGroupCode = useMemo(
    () => txGroupCode || groupCode || "",
    [txGroupCode, groupCode]
  );

  const effectiveGroupValues = useMemo(
    () => (txSelected && txSelected.length > 0) ? txSelected : (groupValues ?? []),
    [txSelected, groupValues]
  );
  const lastQueryRef = useRef<string>("");
  const keepLeafTaxonsOnly = (selectedCodes: string[], allFilters: Filter[] | null) => {
    if (!allFilters?.length) return selectedCodes;

    const byCode = new Map(allFilters.map(f => [f.code, f]));
    const selectedSet = new Set(selectedCodes);
    const isAncestorOfAnotherSelected = (code: string) => {
      for (const other of selectedCodes) {
        if (other === code) continue;
        let cur = byCode.get(other);
        while (cur?.parentCode) {
          if (cur.parentCode === code) return true;
          cur = byCode.get(cur.parentCode);
        }
      }
      return false;
    };

    return selectedCodes.filter(code => !isAncestorOfAnotherSelected(code));
  };

  const productsQueryKey = useMemo(() => {
    const fv = Array.isArray(effectiveGroupValues) ? effectiveGroupValues.join(";") : "";
    // Si filtersSelected est un tableau d’objets, le JSON.stringify est ok ici
    const fs = JSON.stringify(filtersSelected || []);
    return [
      locale,
      currentPage,
      itemsPerPage,
      effectiveGroupCode,
      fv,
      isEndOfStock ? "1" : "0",
      fs
    ].join("|");
  }, [
    locale,
    currentPage,
    itemsPerPage,
    effectiveGroupCode,
    effectiveGroupValues,
    isEndOfStock,
    filtersSelected
  ]);

  useEffect(() => {
    if (lastQueryRef.current === productsQueryKey) {
      return;
    }
    lastQueryRef.current = productsQueryKey;
    const taxonomyKeyFromUrl =
      Array.isArray(filtersSelected) && filtersSelected.length > 0
        ? filtersSelected[0].index
        : effectiveGroupCode;

    const cleanedTaxons = keepLeafTaxonsOnly(effectiveGroupValues, filters);

    dispatch(
      getProducts(
        locale,
        currentPage,
        itemsPerPage,
        taxonomyKeyFromUrl,
        cleanedTaxons,
        filtersSelected,
        isEndOfStock
      )
    );

  }, [productsQueryKey, dispatch]);



  useEffect(() => {
    dispatch(getProductsFilters(locale));
    dispatch(getTaxons(locale));
  }, [locale]);


  const resetFilters = () => {
    setCurrentPage(1);
    setTxSelected([]);
    setTxParent("");
    setTxGroupCode("");
    setSubTaxParent(undefined);
    setAllSubTaxonomiesSelected([]);
    setSubCounters([]);
    history({ pathname: location.pathname, search: "" });
  };

  const totalItems = products ? products.totalItems : 0;
  const numberOfPages = totalItems ? Math.ceil(totalItems / itemsPerPage) : 1;
  const shouldShowPagination = numberOfPages > 1;

  const showEPIBlock = filtersSelected.some(f => f.values.includes("INDIVIDUAL_PROTECTION_CODE"));

  const [subTaxParent, setSubTaxParent] = useState<Filter | undefined>(undefined);
  const [subCounters, setSubCounters] = useState<{ parentCode: string; taxonsCodes: string[]; count: number; }[]>([]);
  const [allSubTaxonomiesSelected, setAllSubTaxonomiesSelected]= useState<Filter[]>([])
  const isRootCatalogue = !effectiveGroupCode && effectiveGroupValues.length === 0;



  useEffect(() => {
    setTxSelected(groupValues ?? []);
    setTxParent(groupValues?.[0] ?? "");
    setTxGroupCode(groupCode ?? "");
  }, [groupValues, groupCode]);

  const lastParentRequestRef = useRef(0);

  const handleShowAndGetSubTaxons = async (parentCode: string) => {
    // si on reclique la même catégorie -> toggle (fermer)
    if (subTaxParent?.code === parentCode) {
      setSubTaxParent(undefined);
      setAllSubTaxonomiesSelected([]);
      setSubCounters([]);
      return;
    }

    const f = filters?.find(x => x.code === parentCode);
    if (!f) return;

    // ✅ Reset des états liés au parent précédent
    setSubCounters([]);
    setAllSubTaxonomiesSelected([]);

    // ✅ Ouvre le parent + filtre immédiatement sur le parent
    setSubTaxParent({ ...f });
    setTxGroupCode(f.groupCode ?? "");
    setTxSelected([f.code]);
    setCurrentPage(1);

    // ✅ Anti-race condition : on ignore les réponses "anciennes"
    const reqId = ++lastParentRequestRef.current;
    try {
      await dispatch(getTaxons(locale, parentCode) as any);
      if (reqId !== lastParentRequestRef.current) return; // une autre catégorie a été cliquée entre-temps
    } catch (e) {
      console.error("Error getTaxons", e);
    }
  };


  const chekIfSubTaxonsParentIsDifferent = (val: "" | Filter) => {
    if (val === "") setSubTaxParent(undefined);
    else setSubTaxParent({ ...val });
  };
  useEffect(() => {
    if (filters && subTaxParent) {
      const selected = filters.filter(a => a.parentCode === subTaxParent.code);
      setAllSubTaxonomiesSelected(selected);
    } else {
      setAllSubTaxonomiesSelected([]);
    }
  }, [filters, subTaxParent]);

  return (
    <>
      <PageTitle catalogueName={groupValues?.[0] ?? ""} filters={filters} isforProductDetail={false} productmetaKeywords="" productmetaDescription="" />
      {isRootCatalogue && <CategoriesSecurityEquipment />}
      <section className={groupValues?.length ? "bg-circle pdt-60" : "bg-circle"}>
        <div className="container pb-5">
          <div className="row">

            <TaxonomiesProducts
             setGroupCode={setTxGroupCode}
              setTaxonomiesSelected={setTxSelected}
               filtersSelectedParent={txParent}
               setGroupCodeParent={setTxGroupCode}
               setFiltersSelectedParent={setTxParent}
               taxonomiesSelected={txSelected}
               handleShowAndGetSubTaxons={handleShowAndGetSubTaxons}
               chekIfSubTaxonsParentIsDifferent={chekIfSubTaxonsParentIsDifferent}
               subTaxonsParent={subTaxParent}
               arraySubTaxonsCounter={subCounters}
               setArraySubTaxonsCounter={setSubCounters}
               showEPISubTaxonomies={showEPIBlock}
               isEndOfStock={isEndOfStock}
             />

            <aside className="col-lg-3 pb-90">
              {showEPIBlock && <CategoriesIndividualEquipment showInSidebar={true} isEndOfStock={isEndOfStock} />}
              <ProductsFilters
                resetFilters={resetFilters}
                filtersSelected={filtersSelected}
                setFiltersSelected={() => {}}
                pendingFilters={filtersSelected}
                setPendingFilters={() => {}}
                setGroupCode={() => {}}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                groupCode={groupCode ?? ""}
                taxonomiesSelected={groupValues ?? []}
                filtersSelectedParent={groupValues?.[0] ?? ""}
                filters={filters}
                newViewProduct={newViewProduct}
                isEndOfStock={isEndOfStock}
              />
            </aside>

            <main className="col-lg-9" id="topPage">
              {subTaxParent && subTaxParent.code !== "" ? (
                <SubTaxonomiesProducts
                  setGroupCode={setTxGroupCode}
                  setTaxonomiesSelected={setTxSelected}
                  filtersSelectedParent={txParent}
                  taxonomiesSelected={txSelected}
                  subTaxonsParent={subTaxParent}
                  filters={filters}
                  arraySubTaxonsCounter={subCounters}
                  setArraySubTaxonsCounter={setSubCounters}
                  allSubTaxonomiesSelected={allSubTaxonomiesSelected}
                  filtersSelected={filtersSelected}
                  history={history}
                />
              ) : null}

              {isRequestingGetProducts ? <SpinnerOnTop /> : (
                <ProductsCatalogue
                  products={products}
                  totalItems={totalItems}
                  resetFilters={resetFilters}
                  filtersSelected={filtersSelected}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  groupCode={effectiveGroupCode}
                  taxonomiesSelected={effectiveGroupValues}
                  filtersSelectedParent={groupValues?.[0] ?? ""}
                  setNewViewProduct={setNewViewProduct}
                  newViewProduct={newViewProduct}
                  pendingFilters={filtersSelected}
                  setFiltersSelected={() => {}}
                  setIsEndOfStock={() => {}}
                  isEndOfStock={isEndOfStock}
                  groupCodeParent={groupCode ?? ""}
                  setCurrentPage={setCurrentPage}
                  topPageId="topPage"
                  isLoading={isRequestingGetProducts}
                />
              )}
            </main>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductsListContainer;
